import {
  emitDonorCaptureDiagnostic,
  fetchDonorCaptureProgress,
  GuestCaptureProgressResponse,
  GuestCaptureUploadResponse,
  uploadDonorCaptureSample,
  uploadDonorListenerClip,
} from '../api/donorSessionApi';
import type { ApiError } from '../api/http';
import {
  CaptureClient,
  CaptureProgress,
  CaptureQualityReport,
  CaptureSessionConfig,
  CaptureTarget,
  CaptureUploadResult,
  VoiceCaptureTarget,
} from './captureClient';
import { fetchSessionConfig, SessionConfig } from './sessionApi';

const stages = new Set([
  'details', 'consent', 'voice_samples', 'listener_clips',
  'complete', 'building', 'ready', 'blocked',
]);
const buildStatuses = new Set([
  'pending', 'queue_pending', 'building', 'ready', 'failed', 'blocked',
]);
const clipKeys = new Set(['intro', 'name', 'goodnight']);
const publicId = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

interface DonorCaptureClientOptions {
  initialProgress: GuestCaptureProgressResponse;
  onLifecycleError?: (error: ApiError) => void;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function hasExactKeys(value: Record<string, unknown>, expected: string[]): boolean {
  const keys = Object.keys(value).sort();
  const wanted = [...expected].sort();
  return keys.length === wanted.length && keys.every((key, index) => key === wanted[index]);
}

export function validateGuestCaptureProgress(
  value: unknown,
): GuestCaptureProgressResponse {
  if (!isRecord(value) || !hasExactKeys(value, [
    'version', 'stage', 'voice_samples', 'listeners',
    'durable_capture_complete', 'build_status', 'draft_scope_key',
  ])) throw new Error('Donor capture progress is unavailable.');
  if (
    value.version !== 1
    || typeof value.stage !== 'string'
    || !stages.has(value.stage)
    || typeof value.build_status !== 'string'
    || !buildStatuses.has(value.build_status)
    || typeof value.durable_capture_complete !== 'boolean'
    || typeof value.draft_scope_key !== 'string'
    || value.draft_scope_key.length < 20
    || value.draft_scope_key.length > 100
    || /\s/.test(value.draft_scope_key)
  ) throw new Error('Donor capture progress is unavailable.');

  const samples = value.voice_samples;
  if (!isRecord(samples) || !hasExactKeys(samples, ['expected', 'persisted_indexes', 'complete'])) {
    throw new Error('Donor capture progress is unavailable.');
  }
  if (
    samples.expected !== 6
    || typeof samples.complete !== 'boolean'
    || !Array.isArray(samples.persisted_indexes)
    || !samples.persisted_indexes.every((index) => (
      Number.isInteger(index) && Number(index) >= 1 && Number(index) <= 6
    ))
    || new Set(samples.persisted_indexes).size !== samples.persisted_indexes.length
  ) throw new Error('Donor capture progress is unavailable.');
  if (samples.complete !== (samples.persisted_indexes.length === samples.expected)) {
    throw new Error('Donor capture progress is unavailable.');
  }

  if (!Array.isArray(value.listeners) || value.listeners.length > 20) {
    throw new Error('Donor capture progress is unavailable.');
  }
  const seenListeners = new Set<string>();
  for (const listener of value.listeners) {
    if (!isRecord(listener) || !hasExactKeys(listener, [
      'invitation_listener_id', 'display_name', 'persisted_clip_keys', 'complete',
    ])) throw new Error('Donor capture progress is unavailable.');
    if (
      typeof listener.invitation_listener_id !== 'string'
      || !publicId.test(listener.invitation_listener_id)
      || seenListeners.has(listener.invitation_listener_id)
      || typeof listener.display_name !== 'string'
      || !listener.display_name.trim()
      || listener.display_name.length > 100
      || listener.display_name !== listener.display_name.trim()
      || typeof listener.complete !== 'boolean'
      || !Array.isArray(listener.persisted_clip_keys)
      || !listener.persisted_clip_keys.every((key) => typeof key === 'string' && clipKeys.has(key))
      || new Set(listener.persisted_clip_keys).size !== listener.persisted_clip_keys.length
    ) throw new Error('Donor capture progress is unavailable.');
    if (listener.complete !== (listener.persisted_clip_keys.length === 3)) {
      throw new Error('Donor capture progress is unavailable.');
    }
    seenListeners.add(listener.invitation_listener_id);
  }
  if (
    value.durable_capture_complete
    && (!samples.complete || value.listeners.some((listener) => !listener.complete))
  ) throw new Error('Donor capture progress is unavailable.');
  return value as unknown as GuestCaptureProgressResponse;
}

function normalizeConfig(config: SessionConfig | null): CaptureSessionConfig | null {
  if (!config) return null;
  return {
    captureVersion: config.capture_version,
    qaVersion: config.qa_version,
    micCheck: {
      roomToneS: config.mic_check.room_tone_s,
      speechS: config.mic_check.speech_s,
      sentence: config.mic_check.sentence,
      snrPassDb: config.mic_check.snr_pass_db,
      snrWarnDb: config.mic_check.snr_warn_db,
      peakMaxDb: config.mic_check.peak_max_db,
    },
    takePrompts: config.take_prompts.map((prompt) => ({
      style: prompt.style,
      title: prompt.title,
      coaching: prompt.coaching,
      text: prompt.text,
      minS: prompt.min_s,
      maxS: prompt.max_s,
    })),
    verbatimPrompts: config.verbatim_prompts.map((prompt) => ({
      key: prompt.key,
      title: prompt.title,
      template: prompt.template,
      coaching: prompt.coaching,
    })),
  };
}

function normalizeQuality(
  result: GuestCaptureUploadResponse,
): CaptureQualityReport {
  return {
    durationSeconds: result.quality_report.duration_seconds,
    loudnessDb: result.quality_report.loudness_db,
    signalQuality: result.quality_report.signal_quality ?? undefined,
    clipping: result.quality_report.clipping ?? undefined,
    clippingPercent: result.quality_report.clipping_percent ?? undefined,
    silencePercent: result.quality_report.silence_percent ?? undefined,
    snrDb: result.quality_report.snr_db ?? undefined,
    sampleRate: result.quality_report.sample_rate ?? undefined,
  };
}

function normalizeUpload(value: unknown, expectedSampleIndex: number): CaptureUploadResult {
  if (!isRecord(value) || !hasExactKeys(value, [
    'accepted', 'sample_index', 'quality_report', 'rejection_reasons',
    'suggestions', 'message', 'storage_confirmed',
  ])) throw new Error('Donor recording result is unavailable.');
  const result = value as unknown as GuestCaptureUploadResponse;
  const quality = result.quality_report;
  if (
    typeof result.accepted !== 'boolean'
    || typeof result.storage_confirmed !== 'boolean'
    || !Number.isInteger(result.sample_index)
    || result.sample_index !== expectedSampleIndex
    || typeof result.message !== 'string'
    || result.message.length > 200
    || !isRecord(quality)
    || !hasExactKeys(quality, [
      'duration_seconds', 'loudness_db', 'signal_quality', 'clipping',
      'clipping_percent', 'silence_percent', 'snr_db', 'sample_rate',
    ])
    || typeof quality.duration_seconds !== 'number'
    || !Number.isFinite(quality.duration_seconds)
    || typeof quality.loudness_db !== 'number'
    || !Number.isFinite(quality.loudness_db)
    || (quality.signal_quality != null && typeof quality.signal_quality !== 'string')
    || (quality.clipping != null && typeof quality.clipping !== 'string')
    || (quality.clipping_percent != null && typeof quality.clipping_percent !== 'number')
    || (quality.silence_percent != null && typeof quality.silence_percent !== 'number')
    || (quality.snr_db != null && typeof quality.snr_db !== 'number')
    || (quality.sample_rate != null && !Number.isInteger(quality.sample_rate))
    || (result.rejection_reasons != null && (
      !Array.isArray(result.rejection_reasons)
      || !result.rejection_reasons.every((reason) => typeof reason === 'string' && reason.length <= 200)
    ))
    || (result.suggestions != null && (
      !Array.isArray(result.suggestions)
      || !result.suggestions.every((suggestion) => typeof suggestion === 'string' && suggestion.length <= 200)
    ))
  ) throw new Error('Donor recording result is unavailable.');
  return {
    accepted: result.accepted,
    storageConfirmed: result.storage_confirmed,
    qualityReport: normalizeQuality(result),
    rejectionReasons: result.rejection_reasons,
    suggestions: result.suggestions,
  };
}

function isApiError(value: unknown): value is ApiError {
  return isRecord(value) && typeof value.code === 'string' && 'status' in value;
}

function requireGuestTarget(target: CaptureTarget): void {
  if (target.voiceId !== undefined) {
    throw new Error('Donor capture target contained a prohibited voice identifier.');
  }
  if (target.kind === 'listener' && !publicId.test(target.listenerId)) {
    throw new Error('Donor listener target is unavailable.');
  }
}

function normalizeProgressForTarget(
  target: CaptureTarget,
  raw: GuestCaptureProgressResponse,
): CaptureProgress {
  requireGuestTarget(target);
  if (target.kind === 'voice') {
    return {
      target,
      complete: raw.voice_samples.complete,
      completedTakeIndexes: raw.voice_samples.persisted_indexes.map((index) => index - 1),
      completedClipKeys: [],
      missing: raw.voice_samples.complete ? [] : ['voice_samples'],
    };
  }
  const listener = raw.listeners.find(
    (candidate) => candidate.invitation_listener_id === target.listenerId,
  );
  if (!listener) throw new Error('Donor capture progress did not match the listener.');
  return {
    target,
    complete: listener.complete,
    completedTakeIndexes: [],
    completedClipKeys: [...listener.persisted_clip_keys],
    missing: listener.complete ? [] : ['listener_clips'],
  };
}

export function createDonorSessionCaptureClient(
  options: DonorCaptureClientOptions,
): CaptureClient {
  const initial = validateGuestCaptureProgress(options.initialProgress);
  const draftScopeKey = initial.draft_scope_key;

  const guarded = async <T,>(operation: () => Promise<T>): Promise<T> => {
    try {
      return await operation();
    } catch (error) {
      if (
        isApiError(error)
        && (error.status === 401 || error.status === 403 || error.status === 409 || error.status === 410)
      ) options.onLifecycleError?.(error);
      throw error;
    }
  };
  const progress = () => guarded(async () => {
    const next = validateGuestCaptureProgress(await fetchDonorCaptureProgress());
    if (next.draft_scope_key !== draftScopeKey) {
      throw new Error('Donor capture session changed unexpectedly.');
    }
    return next;
  });

  return {
    emitDiagnostic: emitDonorCaptureDiagnostic,
    draftScopeKey,
    voiceTargetRequiresId: false,
    async fetchSessionConfig() {
      return normalizeConfig(await fetchSessionConfig());
    },
    async ensureVoice(_label: string): Promise<VoiceCaptureTarget> {
      const current = await progress();
      if (current.stage !== 'voice_samples') {
        throw new Error('Donor voice capture is unavailable.');
      }
      return { kind: 'voice' };
    },
    async fetchProgress(target: CaptureTarget) {
      return normalizeProgressForTarget(target, await progress());
    },
    async uploadTake(target, take, blob) {
      requireGuestTarget(target);
      if (target.kind !== 'voice') {
        throw new Error('Donor voice sample target is unavailable.');
      }
      return normalizeUpload(await guarded(() => uploadDonorCaptureSample({
        sampleIndex: take.sampleIndex,
        style: take.style,
        stats: take.stats,
        blob,
        forced: take.forced,
      })), take.sampleIndex);
    },
    async uploadListenerClip(target, clip, blob) {
      requireGuestTarget(target);
      if (target.kind !== 'listener') {
        throw new Error('Donor listener target is unavailable.');
      }
      return normalizeUpload(await guarded(() => uploadDonorListenerClip({
        invitationListenerId: target.listenerId,
        clipKey: clip.clipKey,
        blob,
        forced: clip.forced,
      })), 0);
    },
    async complete(target) {
      const status = normalizeProgressForTarget(target, await progress());
      if (!status.complete) {
        throw new Error('Donor capture is not durably complete.');
      }
    },
  };
}
